
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { WorkflowCanvas } from '@/components/workflows/WorkflowCanvas';
import { useWorkflowStore } from '@/lib/workflows/store';
import { workflowTemplates } from '@/lib/workflows/templates';
import { createClient } from '@/utils/supabase/server-props';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DesktopComputerIcon } from '@heroicons/react/outline';
import AuthModal from '@/components/AuthModal';
import Seo from '@/components/Seo';
export default function WorkflowTemplatePreviewPage({ isAuthenticated, }: InferGetServerSidePropsType<typeof getServerSideProps>) {
    const router = useRouter();
    const { id } = router.query;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const isMobile = useIsMobile();
    const clearWorkflow = useWorkflowStore((state) => state.clearWorkflow);
    const setNodes = useWorkflowStore((state) => state.setNodes);
    const setEdges = useWorkflowStore((state) => state.setEdges);
    const setWorkflowId = useWorkflowStore((state) => state.setWorkflowId);
    const setWorkflowName = useWorkflowStore((state) => state.setWorkflowName);
    const setWorkflowOwnerId = useWorkflowStore((state) => state.setWorkflowOwnerId);
    const setWorkflowOwnerProfile = useWorkflowStore((state) => state.setWorkflowOwnerProfile);
    const markSaved = useWorkflowStore((state) => state.markSaved);
    const template = id && typeof id === 'string'
        ? workflowTemplates.find((t) => t.id === id)
        : undefined;
    useEffect(() => {
        if (!id || typeof id !== 'string')
            return;
        if (!template) {
            setError('Template not found');
            setLoading(false);
            return;
        }
        // Seed template into workflow store without a workflowId (preview-only)
        clearWorkflow();
        setNodes(template.flow_data?.nodes || []);
        setEdges(template.flow_data?.edges || []);
        setWorkflowId(null);
        setWorkflowName(template.name);
        setWorkflowOwnerId(null);
        setWorkflowOwnerProfile(null);
        markSaved();
        setLoading(false);
    }, [
        id,
        template,
        clearWorkflow,
        setNodes,
        setEdges,
        setWorkflowId,
        setWorkflowName,
        setWorkflowOwnerId,
        setWorkflowOwnerProfile,
        markSaved,
    ]);
    const handleUseTemplate = async () => {
        if (!isAuthenticated) {
            setShowAuthModal(true);
            return;
        }
        if (!id || typeof id !== 'string')
            return;
        if (!template)
            return;
        try {
            const response = await fetch('/api/workflows', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: template.name,
                    description: template.description,
                    category: template.category,
                    flow_data: template.flow_data,
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to create workflow from template');
            }
            const workflow = await response.json();
            router.push(`/workflows/${workflow.id}`);
        }
        catch (err) {
            console.error('Failed to create workflow from template:', err);
            alert('Failed to create workflow. Please try again.');
        }
    };
    const redirectTo = typeof id === 'string' ? `/workflows/templates/${id}` : '/workflows';
    const seo = (<Seo templateTitle={template
            ? `${template.name} - AI Workflow Template`
            : 'Workflow Template Preview'} description={template?.description} image={template?.backgroundImage}/>);
    if (isMobile) {
        return (<>
        {seo}
        <div className='min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-6'>
          <div className='text-center max-w-sm'>
            <DesktopComputerIcon className='w-16 h-16 text-primary-600 dark:text-primary-400 mx-auto mb-4'/>
            <h2 className='text-xl font-semibold text-gray-900 dark:text-white mb-2'>
              Desktop Required
            </h2>
            <p className='text-gray-600 dark:text-gray-400 mb-6'>
              The Workflow Editor is optimized for desktop screens. Please open
              this page on a computer for the best experience.
            </p>
            <button onClick={() => router.push('/workflows')} className='px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors'>
              Back to Workflows
            </button>
          </div>
        </div>

        <AuthModal showModal={showAuthModal} onClose={() => setShowAuthModal(false)} redirectTo={redirectTo}/>
      </>);
    }
    if (loading) {
        return (<>
        {seo}
        <div className='min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900'>
          <div className='text-center'>
            <div className='w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4'/>
            <p className='text-gray-600 dark:text-gray-400'>
              Loading template...
            </p>
          </div>
        </div>
      </>);
    }
    if (error) {
        return (<>
        {seo}
        <div className='min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900'>
          <div className='text-center max-w-md'>
            <svg className='w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4' fill='none' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' viewBox='0 0 24 24' stroke='currentColor'>
              <path d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'/>
            </svg>
            <h2 className='text-xl font-semibold text-gray-900 dark:text-white mb-2'>
              Failed to Load Template
            </h2>
            <p className='text-gray-600 dark:text-gray-400 mb-6'>{error}</p>
            <button onClick={() => router.push('/workflows')} className='px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors'>
              Back to Workflows
            </button>
          </div>
        </div>
      </>);
    }
    return (<>
      {seo}

      <div className='h-screen w-full overflow-hidden'>
        <WorkflowCanvas onUseTemplate={handleUseTemplate} readOnly/>
      </div>

      <AuthModal showModal={showAuthModal} onClose={() => setShowAuthModal(false)} redirectTo={redirectTo}/>
    </>);
}
async function getServerSideProps(context: GetServerSidePropsContext) {
    const supabase = createClient(context);
    const { data: { user }, } = await supabase.auth.getUser();
    return {
        props: {
            isAuthenticated: !!user,
        },
    };
}

    async function __Next_Translate__getServerSideProps__1a167d9f580__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/workflows/templates/[id]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a167d9f580__ as getServerSideProps }
  